import cx from "classnames"
import { AlertCircle } from "lucide-react"
import type { ReactNode } from "react"

interface PillItem {
    id: string
    icon: ReactNode
    label: string
    isError?: boolean
    content: ReactNode
}

interface PillGroupProps {
    items: PillItem[]
    expandedId: string | null
    onToggle: (id: string) => void
}

function Pill({ icon, label, isExpanded, isError, onClick }: { icon: ReactNode; label: string; isExpanded: boolean; isError?: boolean; onClick: () => void }) {
    return (
        <button
            type="button"
            className={cx(
                "btn flex items-center gap-1.5 border border-transparent px-2 py-1 text-xs transition-colors focus-visible:border-primary/40 focus-visible:bg-base-200/60 focus-visible:outline-none",
                isExpanded
                    ? "border-primary/40 bg-primary/10 text-base-content"
                    : "bg-transparent text-muted hover:border-border/50 hover:bg-base-200/50 hover:text-base-content"
            )}
            onClick={onClick}
        >
            {icon}
            <span className="font-medium truncate max-w-[200px]">{label}</span>
            {isError && <AlertCircle size="0.85em" className="text-error flex-shrink-0" />}
        </button>
    )
}

export function PillGroup({ items, expandedId, onToggle }: PillGroupProps) {
    const expandedItem = items.find((item) => item.id === expandedId)

    return (
        <div className="py-1">
            <div className="flex flex-wrap gap-1">
                {items.map((item) => (
                    <Pill
                        key={item.id}
                        icon={item.icon}
                        label={item.label}
                        isExpanded={expandedId === item.id}
                        isError={item.isError}
                        onClick={() => onToggle(item.id)}
                    />
                ))}
            </div>
            {expandedItem && <div className="mt-1 border border-border bg-base-100">{expandedItem.content}</div>}
        </div>
    )
}
